<template>
  <div>
    <div v-if="isLoading" class="text-center py-12">
      <b-spinner variant="primary" label="กำลังโหลด..."></b-spinner>
      <span class="text-muted font-weight-bold mt-3 d-block">กำลังโหลดข้อมูลแต้มหมดอายุ...</span>
    </div>

    <p v-else-if="!pointExpiration" class="text-center text-muted py-12">ไม่พบข้อมูลแต้ม</p>

    <template v-else>
      <b-row class="mb-8">
        <b-col v-for="card in summaryCards" :key="card.label" md="4" class="mb-4">
          <div class="bg-light rounded p-5 h-100">
            <span class="text-muted font-weight-bold d-block mb-2">{{ card.label }}</span>
            <span class="font-size-h3 font-weight-bolder" :class="card.valueClass">{{ card.value }}</span>
            <span class="text-muted font-size-sm d-block mt-1">{{ card.hint }}</span>
          </div>
        </b-col>
      </b-row>

      <h5 class="font-weight-bolder mb-1">รายละเอียดแต้มที่ใกล้หมดอายุ</h5>
      <span class="text-muted font-weight-bold font-size-sm d-block mb-5">
        เมื่อแลกแต้ม ระบบตัดก้อนที่ใกล้หมดอายุก่อน และใช้แต้มที่ไม่มีวันหมดอายุเป็นลำดับสุดท้าย
      </span>
      <b-form @submit.prevent="applyFilters">
        <b-row class="align-items-end">
          <b-col md="3">
            <b-form-group label="ประเภทรายการ" label-size="sm">
              <b-form-select v-model="filterForm.type" size="sm" :options="typeFilterOptions" />
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="ตั้งแต่วันที่" label-size="sm">
              <b-form-input
                v-model="filterForm.dateFrom"
                type="date"
                size="sm"
                :max="filterForm.dateTo || null"
                :state="dateRangeError ? false : null"
              />
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="ถึงวันที่" label-size="sm">
              <b-form-input
                v-model="filterForm.dateTo"
                type="date"
                size="sm"
                :min="filterForm.dateFrom || null"
                :state="dateRangeError ? false : null"
              />
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group>
              <b-button type="submit" variant="primary" size="sm" class="font-weight-bold mr-2" :disabled="!!dateRangeError">
                ค้นหา
              </b-button>
              <b-button variant="light" size="sm" class="font-weight-bold" @click="clearFilters">
                ล้างตัวกรอง
              </b-button>
            </b-form-group>
          </b-col>
        </b-row>
        <span v-if="dateRangeError" class="text-danger font-size-sm d-block mt-n4 mb-4">{{ dateRangeError }}</span>
      </b-form>
      <div class="table-responsive">
        <b-table
          :items="batchRows"
          :fields="batchFields"
          :busy="isBusy"
          :borderless="true"
          head-variant="light"
          show-empty
          :empty-text="hasAppliedFilters ? 'ไม่พบก้อนแต้มตามเงื่อนไขที่เลือก' : 'ไม่พบข้อมูลแต้ม'"
        >
          <template #cell(source_type)="data">
            <b-badge :variant="pointSourceDisplay(data.item.source_type).variant">
              {{ pointSourceDisplay(data.item.source_type).text }}
            </b-badge>
          </template>
          <template #cell(received_at)="data">
            {{ formatThaiDate(data.item.received_at) }}
          </template>
          <template #cell(earned_amount)="data">
            {{ formatPoint(data.item.earned_amount) }}
          </template>
          <template #cell(redeemed_amount)="data">
            {{ formatPoint(data.item.redeemed_amount) }}
          </template>
          <template #cell(expired_amount)="data">
            <span :class="{ 'text-danger': data.item.expired_amount > 0 }">
              {{ formatPoint(data.item.expired_amount) }}
            </span>
          </template>
          <template #cell(remaining)="data">
            <span class="font-weight-bolder">{{ formatPoint(data.item.remaining) }}</span>
          </template>
          <template #cell(expiration_date)="data">
            <template v-if="data.item.expiration_date">
              {{ formatThaiDate(data.item.expiration_date) }}
              <span v-if="data.item.expiration_months" class="text-muted font-size-sm d-block">อายุ {{ data.item.expiration_months }} เดือน</span>
            </template>
            <span v-else class="text-muted">ไม่มีวันหมดอายุ</span>
          </template>
          <template #cell(status)="data">
            <b-badge :variant="batchStatusDisplay[data.item.status].variant">
              {{ batchStatusDisplay[data.item.status].text }}
            </b-badge>
          </template>
          <template #cell(actions)="data">
            <a
              href="javascript:"
              class="btn btn-icon btn-light btn-sm"
              v-b-tooltip.hover
              title="ดูรายละเอียด"
              @click.prevent.stop="openBatchDetail(data.item)"
            >
              <span class="svg-icon svg-icon-md svg-icon-primary">
                <inline-svg src="media/svg/icons/General/Visible.svg"></inline-svg>
              </span>
            </a>
          </template>
        </b-table>
      </div>
      <point-expire-batch-detail-modal
        :modal-id="batchDetailModalId"
        :batch="selectedBatch"
        :trace-lines="selectedBatchTraceLines"
      />
      <b-row class="justify-content-between mt-2">
        <b-col cols="12" md="4">
          <b-form-group
            :label="$t('PAGE.DEFAULT.TABLE.PER_PAGE')"
            label-cols="5"
            label-size="sm"
            class="mb-0"
          >
            <b-form-select
              :value="perPage"
              size="sm"
              :options="perPageOptions"
              @change="onPerPageChange"
            />
          </b-form-group>
        </b-col>
        <b-col cols="12" md="8">
          <b-pagination
            :value="page"
            :total-rows="pointExpiration.batches.total"
            :per-page="perPage"
            align="right"
            class="mb-0"
            @input="onPageInput"
          />
        </b-col>
      </b-row>
    </template>
  </div>
</template>

<script>
import Swal from "sweetalert2";
import {
  SIGNED_TRANSACTION_TYPES,
  TRANSACTION_TYPE_DISPLAY,
  BATCH_STATUS_DISPLAY,
  remainingPoint,
  resolveBatchStatus,
  formatPoint,
  formatSignedPoint,
  formatThaiDate,
  pointSourceDisplay,
  resolvePointSourceType
} from "../point-expire.helper";
import { fetchMemberPointExpiration } from "../point-expire.api";
import PointExpireBatchDetailModal from "./PointExpireBatchDetailModal.vue";

const EMPTY_FILTERS = { type: null, dateFrom: "", dateTo: "" };

function amountsInBatch(transaction, batchId) {
  if (transaction.allocations) {
    return transaction.allocations
      .filter((allocation) => allocation.batch_id === batchId)
      .map((allocation) => allocation.amount);
  }
  return transaction.batch_id === batchId ? [transaction.amount] : [];
}

export default {
  name: "PointExpireMemberSummary",
  components: {
    PointExpireBatchDetailModal
  },
  props: {
    customerCampaignId: {
      type: [Number, String],
      required: true
    }
  },
  data() {
    return {
      isLoading: true,
      isBusy: false,
      pointExpiration: null,
      page: 1,
      perPage: 25,
      perPageOptions: [25, 50, 100],
      batchStatusDisplay: BATCH_STATUS_DISPLAY,
      batchDetailModalId: "modal-point-expire-batch-detail",
      selectedBatch: null,
      filterForm: { ...EMPTY_FILTERS },
      appliedFilters: { ...EMPTY_FILTERS },
      typeFilterOptions: [
        { value: null, text: "ทั้งหมด" },
        { value: "EARN", text: TRANSACTION_TYPE_DISPLAY.EARN.text },
        { value: "REDEEM", text: TRANSACTION_TYPE_DISPLAY.REDEEM.text },
        { value: "EXPIRE", text: TRANSACTION_TYPE_DISPLAY.EXPIRE.text }
      ],
      batchFields: [
        { key: "source_type", label: "ที่มาของแต้ม", class: "align-middle" },
        { key: "received_at", label: "วันที่ได้รับ", class: "align-middle" },
        { key: "earned_amount", label: "ได้รับ", class: "text-right align-middle" },
        { key: "redeemed_amount", label: "แลกไป", class: "text-right align-middle" },
        { key: "expired_amount", label: "หมดอายุไป", class: "text-right align-middle" },
        { key: "remaining", label: "คงเหลือ", class: "text-right align-middle" },
        { key: "expiration_date", label: "วันหมดอายุ", class: "align-middle" },
        { key: "status", label: "สถานะ", class: "align-middle" },
        { key: "actions", label: "", class: "text-center align-middle" }
      ]
    };
  },
  computed: {
    batchRows() {
      if (!this.pointExpiration) return [];
      return this.pointExpiration.batches.data.map((batch) => ({
        ...batch,
        source_type: resolvePointSourceType(batch),
        remaining: remainingPoint(batch),
        status: resolveBatchStatus(batch)
      }));
    },
    dateRangeError() {
      const { dateFrom, dateTo } = this.filterForm;
      return dateFrom && dateTo && dateFrom > dateTo
        ? "วันที่เริ่มต้นต้องไม่เกินวันที่สิ้นสุด"
        : null;
    },
    hasAppliedFilters() {
      return Object.values(this.appliedFilters).some(Boolean);
    },
    selectedBatchTraceLines() {
      return this.selectedBatch ? this.batchTraceLines(this.selectedBatch) : [];
    },
    summaryCards() {
      const nextExpiration = this.pointExpiration.next_expiration;
      return [
        {
          label: "แต้มที่ใช้ได้",
          value: formatPoint(this.pointExpiration.available_point),
          valueClass: "text-primary",
          hint: `ในนี้ไม่มีวันหมดอายุ ${formatPoint(this.pointExpiration.no_expiration_point)} แต้ม`
        },
        {
          label: "หมดอายุครั้งถัดไป",
          value: nextExpiration ? formatPoint(nextExpiration.amount) : "-",
          valueClass: "text-warning",
          hint: nextExpiration
            ? `วันที่ ${formatThaiDate(nextExpiration.date)}`
            : "ไม่มีแต้มที่จะหมดอายุ"
        },
        {
          label: "หมดอายุไปแล้วทั้งหมด",
          value: formatPoint(this.pointExpiration.total_expired_point),
          valueClass: "text-danger",
          hint: "ลดเฉพาะแต้มที่ใช้ได้ ไม่กระทบแต้มสะสมตลอดชีพและระดับสมาชิก"
        }
      ];
    }
  },
  watch: {
    customerCampaignId() {
      this.page = 1;
      this.loadPointExpiration();
    }
  },
  mounted() {
    this.loadPointExpiration();
  },
  methods: {
    formatPoint,
    formatThaiDate,
    pointSourceDisplay,
    openBatchDetail(batch) {
      this.selectedBatch = batch;
      this.$bvModal.show(this.batchDetailModalId);
    },
    applyFilters() {
      if (this.dateRangeError) return;
      this.appliedFilters = { ...this.filterForm };
      this.page = 1;
      this.loadPointExpiration();
    },
    clearFilters() {
      this.filterForm = { ...EMPTY_FILTERS };
      this.applyFilters();
    },
    onPageInput(nextPage) {
      this.page = nextPage;
      this.loadPointExpiration();
    },
    onPerPageChange(nextPerPage) {
      this.perPage = Number(nextPerPage);
      this.page = 1;
      this.loadPointExpiration();
    },
    loadPointExpiration() {
      this.isLoading = !this.pointExpiration;
      this.isBusy = true;
      fetchMemberPointExpiration(this.customerCampaignId, this.page, this.perPage, this.appliedFilters)
        .then((pointExpiration) => {
          this.pointExpiration = pointExpiration;
        })
        .catch((error) => {
          Swal.fire({
            icon: "error",
            title: "โหลดข้อมูลแต้มหมดอายุไม่สำเร็จ",
            text: error.message,
            confirmButtonText: "ตกลง"
          });
        })
        .finally(() => {
          this.isLoading = false;
          this.isBusy = false;
        });
    },
    batchTraceLines(batch) {
      return this.pointExpiration.transactions
        .filter((transaction) => transaction.id !== batch.earn_transaction_id)
        .flatMap((transaction) =>
          amountsInBatch(transaction, batch.id).map((amount, index) => ({
            key: `${transaction.id}-${batch.id}-${index}`,
            type: transaction.type,
            transactionId: transaction.reference_code,
            displayAmount: SIGNED_TRANSACTION_TYPES.includes(transaction.type)
              ? formatSignedPoint(amount)
              : formatPoint(Math.abs(amount)),
            createdAt: transaction.created_at
          }))
        )
        .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    }
  }
};
</script>
